import { useEffect, useState, forwardRef, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { CollapseContainer, CollapseItem, Container, Icon, Link, Text, useTranslation, useUi } from '@weareconceptstudio/core';

//* HOC's
import { withDataContext } from '@/context';

//* Libs
import { getPortfolios } from '@/libs/get-data';

//* Helpers
import { config } from '@/helpers';

//* Styles
import BurgerMenuStyle from './style';

//* Utils
import headerLogoSVG from '../utils';

//* Data
const instagramUsername = 'avagyanwedding';

const BurgerMenu = forwardRef(({ globalData, isMobileScreen, cleanedPathname }, ref) => {
	const pathname = usePathname();
	const { isBurgerMenuOpen, toggleBurgerMenu, closeBurgerMenu, winWidth } = useUi();
	const { selectedLang } = useTranslation();

	//! States
	const [data, setData] = useState([]);

	//! Data
	useEffect(() => {
		const fetchData = async () => {
			const { data } = await getPortfolios({ lang: selectedLang, params: { latest: true } });
			setData(data);
		};

		fetchData();
	}, [selectedLang]);

	//! Scroll logic
	const handleScroll = useCallback(() => {
		if (document.querySelector('#collapse-item')) {
			const collapseItem = document.querySelector('#collapse-item');

			collapseItem.setAttribute('status', 'close');
			collapseItem.classList.remove('active');
		}

		if (isBurgerMenuOpen) {
			closeBurgerMenu();
		}
	}, [isBurgerMenuOpen, closeBurgerMenu]);

	//! Close burger menu on window width or route/language change
	useEffect(() => {
		if (document.querySelector('#collapse-item')) {
			const collapseItem = document.querySelector('#collapse-item');

			collapseItem.setAttribute('status', 'close');
			collapseItem.classList.remove('active');
		}
	}, [winWidth, pathname, selectedLang, isBurgerMenuOpen]);

	useEffect(() => {
		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [handleScroll]);

	return (
		<BurgerMenuStyle
			ref={ref}
			className={`burger-menu-wrapper ${isBurgerMenuOpen ? 'active' : ''}`}
		>
			<div className='burger-menu-inner-wrapper'>
				<Container
					row
					className='section-container'
				>
					<div className='burger-left-block col-5 col-dx-4 col-m-12'>
						<div className='burger-left-inner-block is-mobile-version'>
							<div
								onClick={() => toggleBurgerMenu()}
								className={`btn-hover-relative-block`}
							>
								<Text
									text={'close'}
									className={`p5 font-neue-roman white-1000 uppercase cursor-pointer menu-close-text anim-state1`}
								/>

								<Text
									text={'close'}
									className={`p5 font-neue-roman white-1000 uppercase cursor-pointer menu-close-text anim-state2`}
								/>
							</div>

							{isMobileScreen ? (
								<>
									<div className='burger-svg-block'>
										<Link
											className='burger-logo-wrapper'
											url={config.routes.home.path}
											onClick={() => {
												if (isBurgerMenuOpen) {
													closeBurgerMenu();
												}
											}}
										>
											{headerLogoSVG}
										</Link>
									</div>

									<div className='burger-instagram-icon-block'>
										<Link
											external
											url={`https://www.instagram.com/${instagramUsername}`}
										>
											<Icon
												name={'instagram'}
												className={'instagram-icon'}
											/>
										</Link>
									</div>
								</>
							) : (
								<div className='left-bottom-wrapper is-mobile-screen'>
									{globalData.phones.numbers.map((phone, index) => {
										return (
											<Link
												external
												key={index + phone}
												url={`tel:${phone}`}
											>
												<Text className='p5 font-neue-roman white-1000 burger-phone-text'>{phone}</Text>
											</Link>
										);
									})}

									<Link
										external
										url={`mailto:${globalData.email}`}
									>
										<Text className='p5 font-neue-roman white-1000 burger-email-text'>{globalData.email}</Text>
									</Link>
								</div>
							)}
						</div>
					</div>

					<div className='burger-right-block col-7 col-dx-8 col-m-12'>
						<div className='burger-right-inner-block'>
							<div className='block1'>
								<div className={`btn-hover-relative-block ${config.routes.home.path == cleanedPathname ? 'active' : ''}`}>
									<Link
										url={config.routes.home.path}
										onClick={() => {
											if (isBurgerMenuOpen) {
												closeBurgerMenu();
											}
										}}
									>
										<Text
											className='p3 font-neue-roman white-1000 burger-menu-item anim-state1'
											text={config.routes.home.name}
										/>

										<Text
											className='p3 font-neue-roman white-1000 burger-menu-item anim-state2'
											text={config.routes.home.name}
										/>
									</Link>
								</div>

								{isMobileScreen ? null : (
									<Link
										external
										url={`https://www.instagram.com/${instagramUsername}`}
										className='is-mobile-screen'
									>
										<Text
											text={`@${instagramUsername}`}
											className={`p5 font-neue-roman white-1000 instagram-username-text`}
										/>
									</Link>
								)}
							</div>

							{/* {data?.length > 0 ? ( */}
							<div className='block2 user-select-none'>
								<CollapseContainer>
									<CollapseItem
										title={
											<div className={`btn-hover-relative-block`}>
												<Text
													className='p3 font-neue-roman white-1000 burger-menu-item anim-state1'
													text={config.routes.portfolio.name}
												/>

												<Text
													className='p3 font-neue-roman white-1000 burger-menu-item anim-state2'
													text={config.routes.portfolio.name}
												/>
											</div>
										}
										description={
											<div className='burger-portfolio-items-wrapper'>
												<div className='portfolio-items-left-block' />

												<div className='portfolio-items-wrapper'>
													{data.map((item) => {
														return (
															<div
																key={item.id + item.title}
																className={`portfolio-item-block ${`${config.routes.portfolio.path}${item.slug}/` == cleanedPathname ? 'active' : ''}`}
															>
																<Link
																	onClick={() => {
																		if (isBurgerMenuOpen) {
																			closeBurgerMenu();
																		}
																	}}
																	url={`${config.routes.portfolio.path}${item.slug}`}
																>
																	<Text
																		className='p5 font-neue-light white-1000 burger-portfolio-item'
																		text={item.title}
																	/>
																</Link>
															</div>
														);
													})}

													{/* {data?.length >= 12 ? ( */}
													<div className={`portfolio-item-block ${config.routes.portfolio.path == cleanedPathname ? 'active' : ''}`}>
														<Link
															onClick={() => {
																if (isBurgerMenuOpen) {
																	closeBurgerMenu();
																}
															}}
															url={config.routes.portfolio.path}
														>
															<Text
																className='p5 font-neue-light white-1000 uppercase explore-more'
																text={'exploreMore'}
															/>
														</Link>
													</div>
													{/* ) : null} */}
												</div>
											</div>
										}
									></CollapseItem>
								</CollapseContainer>
							</div>
							{/* ) : null} */}

							<div className='block3'>
								<div className={`btn-hover-relative-block ${config.routes.aboutUs.path == cleanedPathname ? 'active' : ''}`}>
									<Link
										url={config.routes.aboutUs.path}
										onClick={() => {
											if (isBurgerMenuOpen) {
												closeBurgerMenu();
											}
										}}
									>
										<Text
											className='p3 font-neue-roman white-1000 burger-menu-item anim-state1'
											text={config.routes.aboutUs.name}
										/>

										<Text
											className='p3 font-neue-roman white-1000 burger-menu-item anim-state2'
											text={config.routes.aboutUs.name}
										/>
									</Link>
								</div>
							</div>

							<div className='block4'>
								<div className={`btn-hover-relative-block ${config.routes.faq.path == cleanedPathname ? 'active' : ''}`}>
									<Link
										url={config.routes.faq.path}
										onClick={() => {
											if (isBurgerMenuOpen) {
												closeBurgerMenu();
											}
										}}
									>
										<Text
											className='p3 font-neue-roman white-1000 burger-menu-item anim-state1'
											text={config.routes.faq.name}
										/>
										<Text
											className='p3 font-neue-roman white-1000 burger-menu-item anim-state2'
											text={config.routes.faq.name}
										/>
									</Link>
								</div>
							</div>

							<div className='block5'>
								<div className={`btn-hover-relative-block ${config.routes.contactUs.path == cleanedPathname ? 'active' : ''}`}>
									<Link
										url={config.routes.contactUs.path}
										onClick={() => {
											if (isBurgerMenuOpen) {
												closeBurgerMenu();
											}
										}}
									>
										<Text
											className='p3 font-neue-roman white-1000 burger-menu-item anim-state1'
											text={config.routes.contactUs.name}
										/>
										<Text
											className='p3 font-neue-roman white-1000 burger-menu-item anim-state2'
											text={config.routes.contactUs.name}
										/>
									</Link>
								</div>
							</div>
						</div>

						{isMobileScreen ? (
							<div className='left-bottom-wrapper'>
								{globalData.phones.numbers.map((phone, index) => {
									return (
										<Link
											external
											key={index + phone}
											url={`tel:${phone}`}
										>
											<Text className='p5 font-neue-roman white-1000 burger-phone-text'>{phone}</Text>
										</Link>
									);
								})}

								<Link
									external
									url={`mailto:${globalData.email}`}
								>
									<Text className='p5 font-neue-roman white-1000 burger-email-text'>{globalData.email}</Text>
								</Link>
							</div>
						) : null}
					</div>
				</Container>
			</div>
		</BurgerMenuStyle>
	);
});

export default withDataContext(BurgerMenu, ['globalData']);
